import {
    useState,
    useEffect,
    FormEvent,
    useContext,
    MouseEvent,
    ChangeEvent,
    useCallback,
    createContext,
    FunctionComponent,
} from 'react';

// Apis
import searchApi, { getSearchHistory } from '@Libs/api/user/search';

// Contexts
import { useAlertContext } from '@Components/Alert/Context';
import { useAccountContext } from '@Components/Account/Contexts';
import { getHttpErrorMessage, useHttpContext } from '@Libs/http';

interface ProviderProps {
    type?: string;
    keyword?: string;
    className?: string;
    placeholder?: string;
    showMobile?: boolean;
    showFilter?: boolean;
    buttonStyle?: string;
    btnSearchMobile?: boolean;
    onClickFilter?: () => void;
    onSearch?: (keyword: string) => void;
}

export interface HistorySearchItem {
    id?: string;
    term?: string;
    [key: string]: any;
}

interface InputSearchContextType {
    value: string;
    isEmpty: boolean;
    isFocusSearch: boolean;
    listHistorySearch: Array<HistorySearchItem>;

    handleFocus: () => void;
    handleToggleFocus: () => void;
    handleRemoveValue: () => void;
    handleChooseSearch: (searchValue: string) => void;
    handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
    handleClickSearch: (event: FormEvent<HTMLFormElement>) => void;
    handleDeleteHistory: (event: MouseEvent<HTMLSpanElement>, searchHistoryId: string) => void;
}

const initState: InputSearchContextType = {
    value: '',
    isEmpty: true,
    isFocusSearch: false,
    listHistorySearch: [],

    handleFocus: () => null,
    handleChange: () => null,
    handleToggleFocus: () => null,
    handleRemoveValue: () => null,
    handleClickSearch: () => null,
    handleChooseSearch: () => null,
    handleDeleteHistory: () => null,
};

const InputSearchContext = createContext<InputSearchContextType>(initState);

export const useInputSearch = () => useContext(InputSearchContext);

const useHistorySearchLocalStorage = () => {
    const [historySearch, setHistorySearch] = useState<HistorySearchItem[]>([]);

    useEffect(() => {
        (() => {
            const historySearch = localStorage.getItem('history_search');
            if (historySearch && historySearch.length > 0) {
                setHistorySearch([...JSON.parse(historySearch)]);
            }
        })();
    }, []);

    const handleSetHistorySearch = (listHistorySearch: Array<HistorySearchItem>) => {
        setHistorySearch([...listHistorySearch]);
        localStorage.setItem('history_search', JSON.stringify(listHistorySearch));
    };

    const deleteHistorySearch = (searchHistoryId: number) => {
        const newHistorySearch = [];
        let index = 0;

        JSON.parse(localStorage.getItem('history_search')).forEach((searchItem) => {
            if (searchItem.id !== searchHistoryId) {
                newHistorySearch.push({ ...searchItem, id: index++ });
            }
        });

        setHistorySearch([...newHistorySearch]);
        localStorage.setItem('history_search', JSON.stringify(newHistorySearch));
    };

    return { historySearch, saveHistorySearch: handleSetHistorySearch, deleteHistorySearch };
};

export const Provider: FunctionComponent<ProviderProps> = ({ type, keyword, onSearch, children }) => {
    const { alert } = useAlertContext();
    const { summary } = useAccountContext();

    const { userHttp } = useHttpContext();
    const { historySearch, saveHistorySearch, deleteHistorySearch } = useHistorySearchLocalStorage();

    const { data: searchHistoryData = {}, mutate } = getSearchHistory({ type });

    const listHistorySearch: Array<HistorySearchItem> = summary ? searchHistoryData.history : historySearch;

    const [value, setValue] = useState<string>(keyword || '');
    const [isEmpty, setIsEmpty] = useState<boolean>(true);
    const [isFocusSearch, toggleFocus] = useState<boolean>(false);

    useEffect(() => {
        if (keyword) {
            setIsEmpty(false);
        }
        setValue(keyword || '');
    }, [keyword]);

    useEffect(() => {
        if (value) {
            setIsEmpty(false);
        }
    }, [isFocusSearch, value]);

    const handleToggleFocus = useCallback(() => toggleFocus(false), []);

    const handleFocus = useCallback(() => {
        toggleFocus(true);
    }, []);

    const handleChooseSearch = (searchValue: string) => {
        toggleFocus(false);

        try {
            gtag('event', 'search', {
                search_term: searchValue,
            });
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line no-console
                console.error('handleChooseSearch tracking error: ', error);
            }
        }

        if (onSearch) {
            onSearch(searchValue);
        }
    };

    const handleRemoveValue = useCallback(() => {
        setIsEmpty(true);
        setValue('');
    }, []);

    const handleDeleteHistory = useCallback(
        async (event: MouseEvent<HTMLSpanElement>, searchHistoryId: string) => {
            event.stopPropagation();
            if (summary) {
                handleDeleteWithApi(searchHistoryId);
                return;
            }

            deleteHistorySearch(Number(searchHistoryId));
        },
        [listHistorySearch],
    );

    const handleDeleteWithApi = async (searchHistoryId: string) => {
        try {
            await searchApi(userHttp).deleteSearchHistory(searchHistoryId);
            mutate();
        } catch (error) {
            alert({
                type: 'error',
                title: 'Đã xảy ra lỗi',
                text: getHttpErrorMessage(error),
            });
        }
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const eventValue = event.target.value;
        if (eventValue) {
            if (isEmpty) {
                setIsEmpty(false);
            }
        } else {
            setIsEmpty(true);
            toggleFocus(true);
        }
        setValue(eventValue);
    };

    const saveLocalStorage = () => {
        if (!value) return;

        const historySearch = JSON.parse(localStorage.getItem('history_search')) || [];

        if (historySearch.length < 4) {
            historySearch.unshift({
                term: value,
                id: 3 - historySearch.length,
            });
        } else {
            for (let index = 3; index >= 0; index--) {
                if (index === 0) {
                    historySearch[index] = {
                        term: value,
                        id: 0,
                    };
                } else {
                    historySearch[index] = { ...historySearch[index - 1], id: index };
                }
            }
        }

        saveHistorySearch(historySearch);
    };

    const handleSearch = () => {
        saveLocalStorage();

        try {
            gtag('event', 'search', {
                search_term: value,
            });
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line no-console
                console.error('handleSearch tracking error: ', error);
            }
        }

        if (onSearch) {
            onSearch(value);
        }
    };

    const handleClickSearch = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (value || onSearch) {
                handleToggleFocus();
                handleSearch();
            }
        },
        [value],
    );

    return (
        <InputSearchContext.Provider
            value={{
                value,
                isEmpty,
                isFocusSearch,
                listHistorySearch,

                handleFocus,
                handleChange,
                handleRemoveValue,
                handleClickSearch,
                handleToggleFocus,
                handleChooseSearch,
                handleDeleteHistory,
            }}
        >
            {children}
        </InputSearchContext.Provider>
    );
};

export function withInputSearchManager<P>(WrappedComponent: FunctionComponent): FunctionComponent<P & ProviderProps> {
    return ({ onSearch, ...props }) => {
        return (
            <Provider type={props.type} keyword={props.keyword} onSearch={onSearch}>
                <WrappedComponent {...props} />
            </Provider>
        );
    };
}
